import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChevronRight, ArchiveRestore } from "lucide-react";
import type { FacilityTreeNode } from "../facility-tree.js";
import { flattenVisible, toggle } from "../facility-tree.js";
import { FacilityCountsMeta } from "../FacilityCountsMeta.js";
import { Button } from "@/components/ui/button";
import { FacilityIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

/**
 * The Archived facilities as a COLLAPSIBLE TREE (FN4a rework) — replaces the flat breadcrumb list, where deep archived
 * descendants showed as separate full-path cards and the hierarchy was lost. The forest is rooted at the archived-
 * subtree ROOTS; expanding a node reveals its archived descendants (archiving cascades, so a root's whole subtree is
 * archived). **Collapsed by default**, so the top level is just the handful of archived roots.
 *
 * Restore-only, parity with classic's Archived tab: no star / kebab / nav — an **Unarchive** button on the subtree
 * ROOTS only (descendants restore with their parent, ADR-006). Rendered as a plain **list of disclosures** (the
 * expand/collapse `<button>` carries `aria-expanded`) rather than an ARIA `tree` — a tree role would promise
 * roving-focus arrow-key navigation this list doesn't implement, and its leaf rows carry no focusable element (panel).
 * Reuses {@link flattenVisible}/{@link toggle} for the expand state — the same machinery as the classic tree.
 */
export function ArchivedFacilityTree({
  roots,
  ariaLabel,
  emptyLine,
  getPathLabel,
  getUnarchive,
}: {
  /** The archived forest ({@link buildArchivedForest}); roots are the archived-subtree roots. */
  roots: readonly FacilityTreeNode[];
  ariaLabel: string;
  emptyLine: string;
  /** The "Root / … / Parent" active-ancestor breadcrumb, shown above each ROOT (its position isn't implied by nesting). */
  getPathLabel: (node: FacilityTreeNode) => string;
  /** Unarchive handler for a node, or null when it isn't an archived-subtree ROOT (restore the parent instead). */
  getUnarchive: (node: FacilityTreeNode) => (() => void) | null;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  if (roots.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-16 text-center">
        <FacilityIcon aria-hidden="true" className="size-8 opacity-40" />
        <p className="text-sm">{emptyLine}</p>
      </div>
    );
  }

  const rows = flattenVisible(roots, { isExpanded: (id) => expanded.has(id) });
  return (
    <ul aria-label={ariaLabel} className="flex flex-col gap-1">
      {rows.map(({ node, depth, expanded: isOpen, hasChildren }) => {
        const unarchive = getUnarchive(node);
        // The active-ancestor breadcrumb only on ROOTS — nested rows get their context from the tree indentation.
        const path = depth === 0 ? getPathLabel(node) : "";
        return (
          <li
            key={node.id}
            className="flex flex-col"
            // Depth indent — the nesting the flat list couldn't show.
            style={{ paddingInlineStart: `${depth * 1.5}rem` }}
          >
            {path ? <p className="text-muted-foreground truncate px-1 pt-1 text-xs">{path}</p> : null}
            <div className="border-border bg-muted hover:bg-background dark:hover:bg-card flex min-h-12 flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border px-3 py-2 transition-colors sm:flex-nowrap">
              <div className="flex min-w-0 items-center gap-1">
                {/* Expand/collapse — only when the node has archived children; a leaf gets a spacer to keep names aligned.
                    `aria-expanded` sits on the button (a disclosure), so the row needs no tree-role keyboard contract. */}
                {hasChildren ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0 rounded-full"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? t("facilitySelection.nav.collapseNode", { name: node.name }) : t("facilitySelection.nav.expandNode", { name: node.name })}
                    onPress={() => setExpanded((s) => toggle(s, node.id))}
                  >
                    <ChevronRight aria-hidden="true" className={cn("size-4 transition-transform", isOpen && "rotate-90")} />
                  </Button>
                ) : (
                  <span aria-hidden="true" className="size-7 shrink-0" />
                )}
                <div className="min-w-0">
                  <h3 className="min-w-0 truncate text-sm font-semibold">{node.name}</h3>
                  <FacilityCountsMeta
                    className="mt-0.5"
                    subfacilities={node.childCount}
                    categories={node.categoryCount ?? 0}
                    manuals={node.manualCount ?? 0}
                  />
                </div>
              </div>
              {/* Unarchive on the subtree ROOT only (restore-only — no star/kebab/nav; ADR-006). */}
              {unarchive ? (
                <Button variant="outline" size="sm" className="shrink-0 rounded-full max-sm:w-full" aria-label={t("facilitySelection.nav.unarchiveNode", { name: node.name })} onPress={unarchive}>
                  <ArchiveRestore aria-hidden="true" className="size-3.5" />
                  {t("facilitySelection.nav.unarchive")}
                </Button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
